<template>
  <v-container v-if="model?.id !== '0'">
    <v-row>
      <v-col>
        <p>{{ props.field.properties.helpText }}</p>
      </v-col>
    </v-row>
    <v-row class="columns">
      <v-col class="column">
        <UploadForm
          :title="props.field.properties.title"
          :drop-file-text="props.field.properties.dropFileText"
          :upload-url="props.field.properties.uploadUrl"
          :extensions="props.field.properties.extensions"
          :accept="props.field.properties.accept"
          :name="props.field.properties.name"
          :multiple="false"
          :auto-upload="true"
          :confirm-upload-message="props.field.properties.confirmUploadMessage"
          @input="updatedValue"
        />
      </v-col>
    </v-row>
    <template v-if="model.original_file !== null">
      <v-row>
        <v-col>
          <h5>Aktueller Ausschnitt:</h5>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <figure v-if="processedDownloadUrl !== null">
            <img
              :src="processedDownloadUrl"
              class="cropped-image"
              alt="Cropped Image"
            />
          </figure>

          <v-alert v-else type="warning">Kein Bildauschnitt gewählt.</v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn-group>
            <v-btn
              prepend-icon="mdi-crop"
              type="button"
              color="primary"
              @click="cropModalActive = true"
            >
              Ausschnitt wählen
            </v-btn>
            <v-btn
              prepend-icon="mdi-delete-forever"
              type="button"
              color="error"
              @click="askDeleteImage"
            >
              Grundriss Löschen
            </v-btn>
          </v-btn-group>
        </v-col>
      </v-row>
    </template>
    <CropModal
      v-if="cropModalActive && originalDownloadUrl"
      v-model:is-active="cropModalActive"
      :original-download-url="originalDownloadUrl"
      @save="cropImage"
      @cancel="cropModalActive = false"
    />
  </v-container>
  <v-alert v-else type="info"
    >Der Stockwerkplan kann nach dem Erstellen des Stockwerks hochgeladen
    werden.</v-alert
  >
  <LoadingOverlay v-model:loading="loading" />
</template>

<script setup lang="ts">
import axios from 'axios'
import { onMounted, type Ref, ref } from 'vue'
import { useSnackbar } from 'vuetify-use-dialog'
import { useStore } from 'vuex'

import { apiClientV2 } from '@/api/ApiClientV2'
import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import UploadForm, { UploadState } from '@/components/common/UploadForm.vue'
import { getModelClass } from '@/models/objectRegistry'
import { useConfirmationDialog } from '@/util/composables/useConfirmationDialog'

import CropModal from './CropModal.vue'
import { SchemaFormKitProps } from './formkit/types'

const createSnackbar = useSnackbar()
const store = useStore()
const model = defineModel<object>({ default: null }) as Ref<
  Record<string, string> & { id: string }
>

const props = defineProps<SchemaFormKitProps>()
const modelClass = getModelClass(props.field.properties.modelClass)
const processedDownloadUrl = ref(null)
const originalDownloadUrl = ref(null)
const exportedDownloadUrl = ref(null)
const cropModalActive = ref(false)
const loading = ref(false)

onMounted(() => {
  loading.value = true
  if (model.value.id !== '0') {
    if (model.value[props.field.key] !== null)
      processedDownloadUrl.value = replacePercentSignInUrlWithModelId(
        props.field.properties.processedDownloadUrl
      )

    if (model.value[props.field.properties.original_key] !== null) {
      originalDownloadUrl.value = replacePercentSignInUrlWithModelId(
        props.field.properties.originalDownloadUrl
      )
      exportedDownloadUrl.value = replacePercentSignInUrlWithModelId(
        props.field.properties.exportedDownloadUrl
      )
    }
  }
  loading.value = false
})

const replacePercentSignInUrlWithModelId = (url: string) => {
  const result = `${url.replace('%', model.value.id)}?${Math.random()}`
  return result
}

function updatedValue(value: UploadState[]) {
  if (value.length > 0) {
    const image = value[0]

    if (image.success) {
      model.value[props.field.key] = image.response.id
      model.value[props.field.properties.processed_file] = image.response.id
      model.value[props.field.properties.original_key] = image.response.id
      originalImageUpdated()
    }
  }
}

const confirm = useConfirmationDialog()

async function askDeleteImage() {
  const isConfirmed = await confirm({
    content: 'Sind Sie sicher, dass Sie den Grundriss löschen wollen?',
  })

  if (isConfirmed) deleteImages()
}

async function deleteImages() {
  loading.value = true

  try {
    model.value[props.field.key] = null
    model.value[props.field.properties.original_key] = null
    model.value.exported_file = null
    model.value.processed_file = null
    await apiClientV2.update(modelClass, model.value)

    originalDownloadUrl.value = null
    processedDownloadUrl.value = null
    exportedDownloadUrl.value = null

    createSnackbar({
      text: 'Gelöscht.',
      snackbarProps: { color: 'green-darken-3' },
    })
  } catch (err) {
    createSnackbar({
      text: 'Fehler beim Löschen.',
      snackbarProps: { color: 'yellow-darken-3' },
    })
  }
  loading.value = false
}

async function originalImageUpdated() {
  if (model.value[props.field.properties.original_key] === null) {
    // File got deleted
    return
  }
  loading.value = true
  try {
    model.value.exported_file = model.value.original_file
    model.value.processed_file = model.value.original_file
    await apiClientV2.update(modelClass, model.value)
    originalDownloadUrl.value = null

    originalDownloadUrl.value = `${replacePercentSignInUrlWithModelId(props.field.properties.originalDownloadUrl)}${model.value[props.field.properties.original_key]}`
    processedDownloadUrl.value = `${replacePercentSignInUrlWithModelId(props.field.properties.processedDownloadUrl)}${model.value[props.field.key]}`
  } catch (error) {
    console.error(error)
    createSnackbar({
      text: 'Fehler beim Hochladen.',
      snackbarProps: { color: 'yellow-darken-3' },
    })
  }
  loading.value = false
}

async function cropImage(blob) {
  loading.value = true
  let formData = new FormData()
  formData.append(props.field.properties.name, blob, 'processed_file.png')

  const axiosConfig = JSON.parse(JSON.stringify(store.state.global.axiosConfig))
  axiosConfig.headers['Content-Type'] = 'multipart/form-data'
  try {
    const result = await axios.post(
      props.field.properties.uploadUrl,
      formData,
      axiosConfig
    )
    if (result.status === 200) {
      model.value[props.field.key] = result.data.id

      await apiClientV2.update(modelClass, model.value)

      processedDownloadUrl.value = `${replacePercentSignInUrlWithModelId(props.field.properties.processedDownloadUrl)}${model.value[props.field.key]}`

      createSnackbar({
        text: 'Bild hochgeladen.',
        snackbarProps: { color: 'green-darken-3' },
      })
      cropModalActive.value = false
    } else {
      throw new Error('')
    }
  } catch (err) {
    console.error(err)
    createSnackbar({
      text: 'Fehler beim Hochladen.',
      snackbarProps: { color: 'yellow-darken-3' },
    })
  }
  loading.value = false
}
</script>

<style lang="scss" scoped>
.cropped-image {
  max-width: 700px;
}
</style>
